import React, { useState, useEffect } from 'react'
import ReactModal from 'react-modal'
import clsx from 'clsx'
import axios from 'axios'
import { Box, CircularProgress } from '@mui/material'
import { Plus, Trash2, Edit, GripVertical, Check } from 'lucide-react'
import { assessmentTemplatesApi } from '../../api/assessments'
import type { Assessment, QuestionResponse, AssessmentTemplate } from '../../types/assessments'
import { STATIC_LIKERT_OPTIONS } from '../../types/assessments'
import { toast } from 'react-toastify'

interface EditAssessmentModalProps {
  isOpen: boolean
  onClose: () => void
  assessment: Assessment
}

export const EditAssessmentModal: React.FC<EditAssessmentModalProps> = ({
  isOpen,
  onClose,
  assessment
}) => {
  const [activeTab, setActiveTab] = useState<'metadata' | 'questions'>('metadata')
  const [loading, setLoading] = useState(false)
  const [questions, setQuestions] = useState<QuestionResponse[]>([])
  const [templates, setTemplates] = useState<AssessmentTemplate[]>([])
  const [editingQuestion, setEditingQuestion] = useState<QuestionResponse | null>(null)
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false)

  // Assessment metadata form
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    category: '',
    icon: '',
    color: '#3498db',
    totalMarks: null as number | null,
    passingMarks: null as number | null,
    durationMinutes: null as number | null,
    status: 'ACTIVE'
  })

  useEffect(() => {
    if (isOpen && assessment) {
      loadData()
    }
  }, [isOpen, assessment])

  const loadData = async () => {
    try {
      setLoading(true)
      const templatesData = await assessmentTemplatesApi.getAll()
      setTemplates(templatesData.data)

      // Convert questions from string array to QuestionResponse objects
      const assessmentQuestions = (assessment as any).questions || []
      const convertedQuestions: QuestionResponse[] = assessmentQuestions.map(
        (q: string, index: number) => ({
          id: index + 1,
          uuid: `temp-${index}`,
          assessmentId: assessment.uuid,
          order: index + 1,
          question: q,
          type: 'MCQ' as const,
          options: STATIC_LIKERT_OPTIONS,
          correctAnswers: [],
          status: 'ACTIVE' as const
        })
      )
      setQuestions(convertedQuestions)

      // Populate form with assessment data
      setFormData({
        title: assessment.title,
        subtitle: assessment.subtitle,
        description: assessment.description,
        category: assessment.category,
        icon: assessment.icon,
        color: assessment.color,
        totalMarks: (assessment as any).totalMarks || null,
        passingMarks: (assessment as any).passingMarks || null,
        durationMinutes: (assessment as any).durationMinutes || null,
        status: (assessment as any).status || 'ACTIVE'
      })
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setLoading(false)
    }
  }

  // Single API call to update assessment with all data
  const handleUpdateAssessment = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth-token')
      const apiUrl = process.env.REACT_APP_RESUME_PARSER_URL || 'http://localhost:4000'

      // Build the payload matching the expected format
      const payload = {
        title: formData.title || '',
        subtitle: formData.subtitle || '',
        description: formData.description || '',
        icon: formData.icon || '📝',
        color: formData.color || '#3498db',
        category: formData.category || '',
        totalMarks: formData.totalMarks,
        passingMarks: formData.passingMarks,
        durationMinutes: formData.durationMinutes,
        status: formData.status,
        questions: questions.map((q) => q.question) // Array of question strings
      }

      const response = await axios.put(`${apiUrl}/api/assessment/${assessment.uuid}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.status === 200 || response.status === 201) {
        toast.success('Assessment updated successfully')
        onClose()
      } else {
        toast.error('Failed to update assessment')
      }
    } catch (error: any) {
      console.error('Failed to update assessment:', error)
      toast.error(error.response?.data?.message || 'Failed to update assessment')
    } finally {
      setLoading(false)
    }
  }

  const handleAddQuestion = () => {
    const newOrder = questions.length > 0 ? Math.max(...questions.map((q) => q.order)) + 1 : 1
    setEditingQuestion({
      id: Date.now(), // Use timestamp as temporary ID for new questions
      uuid: '',
      assessmentId: assessment.uuid,
      order: newOrder,
      question: '',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      correctAnswers: [],
      status: 'ACTIVE'
    })
    setIsQuestionModalOpen(true)
  }

  const handleEditQuestion = (question: QuestionResponse) => {
    setEditingQuestion({ ...question })
    setIsQuestionModalOpen(true)
  }

  // Only update local state - no API call
  const handleDeleteQuestion = (questionId: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return
    setQuestions((prev) => prev.filter((q) => q.id !== questionId))
  }

  // Only update local state - no API call
  const handleSaveQuestion = (questionData: QuestionResponse) => {
    const existingIndex = questions.findIndex((q) => q.id === questionData.id)

    if (existingIndex === -1) {
      // New question - add to local state
      setQuestions((prev) => [...prev, questionData].sort((a, b) => a.order - b.order))
    } else {
      // Update existing question in local state
      setQuestions((prev) =>
        prev
          .map((q) => (q.id === questionData.id ? questionData : q))
          .sort((a, b) => a.order - b.order)
      )
    }
    setIsQuestionModalOpen(false)
    setEditingQuestion(null)
  }

  // Only update local state - no API call
  const handleMoveQuestion = (questionId: number, direction: 'up' | 'down') => {
    const questionIndex = questions.findIndex((q) => q.id === questionId)
    if (questionIndex === -1) return

    const newIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1
    if (newIndex < 0 || newIndex >= questions.length) return

    const newQuestions = [...questions]
    const [moved] = newQuestions.splice(questionIndex, 1)
    newQuestions.splice(newIndex, 0, moved)

    // Update orders locally
    setQuestions(newQuestions.map((q, idx) => ({ ...q, order: idx + 1 })))
  }

  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '15px',
            border: 'none',
            boxShadow: '0 2px 8px 0 #00000029',
            padding: 0,
            maxWidth: '1000px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          }
        }}
        contentLabel='Edit Assessment'
      >
        <div className='card' style={{ margin: 0 }}>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h3 className='card-title fw-bolder mb-0'>Edit Assessment</h3>
            <button
              type='button'
              className='btn-close'
              onClick={onClose}
              aria-label='Close'
            ></button>
          </div>

          <div
            className='card-body p-9'
            style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto' }}
          >
            {/* Tabs */}
            <ul className='nav nav-tabs mb-6'>
              <li className='nav-item'>
                <button
                  className={clsx('nav-link', { active: activeTab === 'metadata' })}
                  onClick={() => setActiveTab('metadata')}
                >
                  Assessment Metadata
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={clsx('nav-link', { active: activeTab === 'questions' })}
                  onClick={() => setActiveTab('questions')}
                >
                  Questions ({questions.length})
                </button>
              </li>
            </ul>

            {loading ? (
              <Box display='flex' justifyContent='center' py={4}>
                <CircularProgress />
              </Box>
            ) : activeTab === 'metadata' ? (
              <MetadataTab formData={formData} setFormData={setFormData} templates={templates} />
            ) : (
              <QuestionsTab
                questions={questions}
                assessment={assessment}
                onAddQuestion={handleAddQuestion}
                onEditQuestion={handleEditQuestion}
                onDeleteQuestion={handleDeleteQuestion}
                onMoveQuestion={handleMoveQuestion}
              />
            )}
          </div>

          <div className='card-footer d-flex justify-content-end gap-2'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleUpdateAssessment}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Assessment'}
            </button>
          </div>
        </div>
      </ReactModal>

      {/* Question Edit Modal */}
      {editingQuestion && (
        <QuestionEditModal
          isOpen={isQuestionModalOpen}
          onClose={() => {
            setIsQuestionModalOpen(false)
            setEditingQuestion(null)
          }}
          question={editingQuestion}
          onSave={handleSaveQuestion}
        />
      )}
    </>
  )
}

// Categories for assessment
const CATEGORIES = [
  'Cyber-Security Maturity',
  'Cloud Adoption Maturity',
  'Cloud Security Maturity',
  'AI Adoption Readiness',
  'Hypervisor → Hyperscaler Licensing',
  'Application Modernisation',
  'Managed Services Readiness',
  'Data Security Maturity'
]

// Predefined colors for color picker
const predefinedColors = [
  { name: 'Red', value: '#e74c3c' },
  { name: 'Blue', value: '#3498db' },
  { name: 'Purple', value: '#9b59b6' },
  { name: 'Green', value: '#27ae60' },
  { name: 'Orange', value: '#f39c12' },
  { name: 'Teal', value: '#1abc9c' },
  { name: 'Pink', value: '#e91e63' },
  { name: 'Indigo', value: '#3f51b5' }
]

interface FormErrors {
  title?: string
  category?: string
}

// Metadata Tab Component
const MetadataTab: React.FC<{
  formData: any
  setFormData: any
  templates: AssessmentTemplate[]
}> = ({ formData, setFormData, templates }) => {
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const handleChange =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setFormData((prev: any) => ({ ...prev, [field]: e.target.value }))
      // Clear error when user starts typing
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    }

  const handleBlur = (field: string) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    // Validate on blur
    if (field === 'title' && !formData.title.trim()) {
      setErrors((prev) => ({ ...prev, title: 'Title is required' }))
    }
    if (field === 'category' && !formData.category.trim()) {
      setErrors((prev) => ({ ...prev, category: 'Category is required' }))
    }
  }

  return (
    <div>
      <h4 className='fw-bold mb-4'>Basic Information</h4>

      <div className='row mb-6'>
        <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Title</label>
        <div className='col-lg-8 fv-row'>
          <input
            type='text'
            value={formData.title}
            onChange={handleChange('title')}
            onBlur={handleBlur('title')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': touched.title && errors.title,
              'is-valid': touched.title && !errors.title
            })}
            placeholder='Enter assessment title'
          />
          {touched.title && errors.title && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' className='text-danger'>
                  {errors.title}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='row mb-6'>
        <label className='col-lg-4 col-form-label fw-semibold fs-6'>Subtitle</label>
        <div className='col-lg-8 fv-row'>
          <input
            type='text'
            value={formData.subtitle}
            onChange={handleChange('subtitle')}
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter assessment subtitle'
          />
        </div>
      </div>

      <div className='row mb-6'>
        <label className='col-lg-4 col-form-label fw-semibold fs-6'>Description</label>
        <div className='col-lg-8 fv-row'>
          <textarea
            value={formData.description}
            onChange={handleChange('description')}
            className='form-control form-control-lg form-control-solid'
            rows={3}
            placeholder='Enter assessment description'
          />
        </div>
      </div>

      <div className='row mb-6'>
        <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Category</label>
        <div className='col-lg-8 fv-row'>
          <select
            value={formData.category}
            onChange={handleChange('category')}
            onBlur={handleBlur('category')}
            className={clsx('form-select form-select-lg form-select-solid', {
              'is-invalid': touched.category && errors.category,
              'is-valid': touched.category && !errors.category
            })}
          >
            <option value=''>Select a category</option>
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          {touched.category && errors.category && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' className='text-danger'>
                  {errors.category}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='row mb-6'>
        <label className='col-lg-4 col-form-label fw-semibold fs-6'>Icon</label>
        <div className='col-lg-8 fv-row'>
          <input
            type='text'
            value={formData.icon}
            onChange={handleChange('icon')}
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter icon (e.g., fa-database, fa-shield-halved, or emoji)'
          />
          <small className='text-muted mt-1 d-block'>
            Enter FontAwesome class (e.g., fa-database) or emoji
          </small>
        </div>
      </div>

      <div className='row mb-6'>
        <label className='col-lg-4 col-form-label fw-semibold fs-6'>Color</label>
        <div className='col-lg-8 fv-row'>
          <div className='d-flex align-items-center gap-3'>
            <input
              type='color'
              value={formData.color}
              onChange={(e) => {
                setFormData((prev: any) => ({ ...prev, color: e.target.value }))
              }}
              style={{
                width: '60px',
                height: '40px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '2px'
              }}
              title='Choose color'
            />
            <input
              type='text'
              value={formData.color}
              onChange={handleChange('color')}
              className='form-control form-control-lg form-control-solid'
              placeholder='#e74c3c'
              style={{ flex: 1 }}
            />
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: formData.color,
                border: '1px solid #ccc',
                flexShrink: 0
              }}
              title={formData.color}
            ></div>
          </div>
          <div className='mt-2'>
            <div className='d-flex gap-2 flex-wrap'>
              {predefinedColors.map((color) => (
                <button
                  key={color.value}
                  type='button'
                  onClick={() => setFormData((prev: any) => ({ ...prev, color: color.value }))}
                  className='btn btn-sm'
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '4px',
                    backgroundColor: color.value,
                    border: formData.color === color.value ? '2px solid #333' : '1px solid #ccc',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Questions Tab Component
const QuestionsTab: React.FC<{
  questions: QuestionResponse[]
  assessment: Assessment
  onAddQuestion: () => void
  onEditQuestion: (q: QuestionResponse) => void
  onDeleteQuestion: (id: number) => void
  onMoveQuestion: (id: number, direction: 'up' | 'down') => void
}> = ({
  questions,
  assessment,
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onMoveQuestion
}) => {
  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h4 className='fw-bold mb-0'>Questions</h4>
        <button type='button' className='btn btn-primary btn-sm' onClick={onAddQuestion}>
          <Plus size={16} className='me-1' />
          Add Question
        </button>
      </div>

      {questions.length === 0 ? (
        <div className='text-center py-8'>
          <p className='text-muted'>No questions yet. Click "Add Question" to get started.</p>
        </div>
      ) : (
        <div>
          {questions.map((question, index) => (
            <div
              key={question.id}
              className='card mb-3'
              style={{ border: `1px solid ${assessment.color}30` }}
            >
              <div className='card-body'>
                <div className='d-flex justify-content-between align-items-start mb-2'>
                  <div className='d-flex align-items-center gap-2'>
                    <GripVertical size={20} className='text-muted' style={{ cursor: 'grab' }} />
                    <span
                      className='badge'
                      style={{ backgroundColor: `${assessment.color}20`, color: assessment.color }}
                    >
                      Q{index + 1}
                    </span>
                    <span className='badge bg-info'>{question.type}</span>
                  </div>
                  <div className='d-flex gap-1'>
                    {index > 0 && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light'
                        onClick={() => onMoveQuestion(question.id, 'up')}
                        title='Move up'
                      >
                        ↑
                      </button>
                    )}
                    {index < questions.length - 1 && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light'
                        onClick={() => onMoveQuestion(question.id, 'down')}
                        title='Move down'
                      >
                        ↓
                      </button>
                    )}
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      onClick={() => onEditQuestion(question)}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-danger'
                      onClick={() => onDeleteQuestion(question.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <p className='fw-semibold mb-2'>{question.question}</p>

                {question.options && question.options.length > 0 && (
                  <div className='mb-2'>
                    {question.options.map((option, optIndex) => (
                      <div
                        key={optIndex}
                        className='d-flex align-items-center gap-2 mb-1 p-2'
                        style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}
                      >
                        <span>{String.fromCharCode(65 + optIndex)}.</span>
                        <span className='flex-grow-1'>{option}</span>
                        {question.correctAnswers?.includes(optIndex) && (
                          <span className='badge bg-success'>
                            <Check size={12} /> Correct
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Question Edit Modal Component
const QuestionEditModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  question: QuestionResponse
  onSave: (q: QuestionResponse) => void
}> = ({ isOpen, onClose, question, onSave }) => {
  const [formData, setFormData] = useState<QuestionResponse>({ ...question })

  useEffect(() => {
    setFormData({ ...question, options: STATIC_LIKERT_OPTIONS })
  }, [question])

  const handleChange =
    (field: keyof QuestionResponse) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.value
      setFormData((prev) => ({ ...prev, [field]: value }))
    }

  const handleToggleCorrectAnswer = (index: number) => {
    if (formData.type === 'SINGLE') {
      // For SINGLE, only one correct answer
      setFormData((prev) => ({ ...prev, correctAnswers: [index] }))
    } else if (formData.type === 'MULTIPLE' || formData.type === 'MCQ') {
      // For MULTIPLE/MCQ, toggle the answer
      const current = formData.correctAnswers || []
      const newAnswers = current.includes(index)
        ? current.filter((ans) => ans !== index)
        : [...current, index]
      setFormData((prev) => ({ ...prev, correctAnswers: newAnswers }))
    }
  }

  const handleSave = () => {
    if (!formData.question.trim()) {
      alert('Question text is required')
      return
    }

    onSave({ ...formData, options: STATIC_LIKERT_OPTIONS })
  }

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '15px',
          border: 'none',
          boxShadow: '0 2px 8px 0 #00000029',
          padding: 0,
          maxWidth: '700px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000
        }
      }}
      contentLabel='Edit Question'
    >
      <div className='card' style={{ margin: 0 }}>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='card-title fw-bolder mb-0'>
            {question.id === 0 ? 'Add Question' : 'Edit Question'}
          </h3>
          <button type='button' className='btn-close' onClick={onClose} aria-label='Close'></button>
        </div>

        <div
          className='card-body p-9'
          style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto' }}
        >
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Question</label>
            <div className='col-lg-8 fv-row'>
              <textarea
                value={formData.question}
                onChange={handleChange('question')}
                className='form-control form-control-lg form-control-solid'
                rows={3}
                placeholder='Enter your question'
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6'>Options</label>
            <div className='col-lg-8 fv-row'>
              <div className='d-flex flex-column gap-2'>
                {STATIC_LIKERT_OPTIONS.map((option, index) => (
                  <div
                    key={index}
                    className='d-flex align-items-center gap-2 p-3'
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <span
                      className='fw-bold'
                      style={{
                        fontSize: '1.2rem',
                        color: '#495057',
                        minWidth: '30px'
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ color: '#495057', flex: 1 }}>{option.split(' - ')[1]}</span>
                  </div>
                ))}
              </div>
              <small className='text-muted mt-2 d-block'>
                These options are standard for all assessment questions and cannot be modified.
              </small>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end gap-2'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Cancel
          </button>
          <button type='button' className='btn btn-primary' onClick={handleSave}>
            Save Question
          </button>
        </div>
      </div>
    </ReactModal>
  )
}
